// @flow

import type { Dispatch } from 'redux';
import { fetchData, logger } from '../../helpers';
import { setUiSetting } from '../uiStore';
import type { ACFFields } from '../../components/formidable/types';
import { OBJECT_TYPE_FORM_FIELDS } from './constants';
import { onPurchase } from '../gtm';

export const FORMIDABLE_FORM_KEY = 'formidable-form';

export const getLoadingStateKey = (id: string): string => `${FORMIDABLE_FORM_KEY}-${id}-loading`;
export const getSubmitSuccessKey = (id: string): string => `${FORMIDABLE_FORM_KEY}-${id}-submit`;
export const getSubmitFailedKey = (id: string): string => `${FORMIDABLE_FORM_KEY}-${id}-failed`;

type FormDate = { day: string, month: string, year: string };
export type Values = { [string]: string | FormDate };

const parseDates = (
  fields: Array<ACFFields>,
  values: Values,
): Object => fields.filter(({ type }) => OBJECT_TYPE_FORM_FIELDS.includes(type))
  .map(({ field_key }) => field_key)
  .reduce((acc, dateField) => {
    const field = values[dateField];
    if (typeof field !== 'object') {
      return acc;
    }

    const {
      day, month, year, ...rest
    } = field;
    if (day && month && year) {
      return {
        ...acc,
        [dateField]: `${year}-${month}-${day}`,
      };
    }

    // Stringify objects so Formidable will accept the value.
    return {
      ...acc,
      [dateField]: rest,
    };
  }, {});

export const submit = (
  id: string,
  values: Values,
  fields?: Array<ACFFields>,
) => async (dispatch: Dispatch<*>) => {
  const body = {
    ...values,
    form_key: id,
  };

  const loadingConst = getLoadingStateKey(id);
  dispatch(setUiSetting(getSubmitSuccessKey(id), false));
  dispatch(setUiSetting(getSubmitFailedKey(id), false));
  dispatch(setUiSetting(loadingConst, true));

  let returnBody = { ...body };

  if (fields) {
    returnBody = { ...returnBody, ...parseDates(fields, values) };
  }

  try {
    await fetchData({
      endpoint: `/api/formidable/${id}`,
      method: 'POST',
      body: returnBody,
    });
    dispatch(setUiSetting(getSubmitSuccessKey(id), true));
    if ('car_configuration' in values) {
      dispatch(onPurchase(new Date().toISOString()));
    }
  } catch (err) {
    logger({
      level: 'emergency',
      message: `Error while submitting formidable form: ${id}`,
      error: err.message,
      stack: err.stack,
    });
    console.error(err);
    dispatch(setUiSetting(getSubmitFailedKey(id), true));
    throw err;
  } finally {
    dispatch(setUiSetting(loadingConst, false));
  }
};

export default null;
